<template>
  <div class="home-echarts-wrap" :style="{ width, height, marginBottom }">
    <div class="home-echarts-header" style="padding: 10px 24px 0 24px;height: auto">
      <h3 class="home-echarts-header__title">{{ title }}</h3>
      <div class="home-echarts-header__controls">
        <a-date-picker
          @change="handleDateChange"
          v-model:value="yearValue"
          :allowClear="false"
          picker="year"
        />
      </div>
    </div>
    <!-- 延迟显示 loading 效果。当 spinning 状态在 delay 时间内结束，则不显示 loading 状态。 -->
    <a-spin :spinning="spinning" :delay="200">
      <!-- echarts 图表 -->
      <div ref="chartRef" class="home-echarts-content" v-show="showECharts"></div>
      <Empty :style="{ width, height }" v-if="!showECharts" />
    </a-spin>
  </div>
</template>

<script setup lang="ts">
import type { PropType, Ref } from 'vue'
import { onMounted, reactive, ref, watch } from 'vue'
import { useECharts } from '@/hooks/useECharts'
import dayjs, { Dayjs } from 'dayjs'
import { getRegionNewActivatedCustomerCount } from '@/api/gucangSales/regionDataBoard'
import { Empty } from 'ant-design-vue'

interface QueryFormType {
  customerRegionEmployeeNoList?: string[]
  startDay?: string
  endDay?: string
}

interface ItemCommonType {
  employeeName?: string // 员工名称
  employeeNo?: string, // 员工工号
  quantity?: number, // 激活数
}

const yearValue = ref<Dayjs>(dayjs())
const props = defineProps({
  title: {
    type: String as PropType<string>,
    default: '新激活客户数'
  },
  width: {
    type: String as PropType<string>,
    default: '100%'
  },
  height: {
    type: String as PropType<string>,
    default: '210px'
  },
  marginBottom: {
    type: String as PropType<string>,
    default: '8px'
  },
  allowClear: {
    type: Boolean as PropType<boolean>,
    default: false
  },
  formState: {
    type: Object as PropType<Object>
  }
})

const spinning = ref(false)
const showECharts = ref(false)
const startMonth = ref<string>(dayjs(yearValue.value).startOf('month').format('YYYY01'))
const endMonth = ref<string>(dayjs(yearValue.value).endOf('month').format('YYYY12'))
const xAxisData = ref<any[]>([])
const queryForm = reactive<QueryFormType>({
  startDay: dayjs(yearValue.value).startOf('year').format('YYYY/MM/DD'),
  endDay: dayjs(yearValue.value).endOf('year').format('YYYY/MM/DD'),
  customerRegionEmployeeNoList: props?.formState?.customerRegionEmployeeNoList || []
})
onMounted(() => {
  handleQueryChange()
})
watch(() => props.formState, (val) => {
  queryForm.customerRegionEmployeeNoList = props?.formState?.customerRegionEmployeeNoList || []
  handleQueryChange()
})
const handleQueryChange = () => {
  xAxisData.value = generateEmployeeArray()
  getData()
}
const handleDateChange = () => {
  queryForm.startDay = dayjs(yearValue.value).startOf('year').format('YYYY/MM/DD')
  queryForm.endDay = dayjs(yearValue.value).endOf('year').format('YYYY/MM/DD')
  startMonth.value = dayjs(yearValue.value).startOf('month').format('YYYY01')
  endMonth.value = dayjs(yearValue.value).endOf('month').format('YYYY12')
  getData()
}
const generateEmployeeArray = () => {
  const datas: any[] = []
  props.formState?.region?.deptScopeInfo.forEach((employee: any) => {
    if (employee) {
      datas.push(employee)
    }
  })
  return datas
}
const getData = () => {
  spinning.value = true
  getRegionNewActivatedCustomerCount(queryForm).then((res) => {
    const activatedCustomerData = res.data as unknown as ItemCommonType[]
    const activatedCustomerList = transChartData(activatedCustomerData)
    if (activatedCustomerList.length) {
      showECharts.value = true
      initChart(activatedCustomerList)
    } else {
      showECharts.value = false
      initChart([])
    }
  }).finally(() => {
    spinning.value = false
  })
}
/// 格式化接口数据和日期对应
const transChartData = (serviceData: ItemCommonType[]) => {
  const matchData: number[] = []
  if (serviceData && serviceData.length > 0) {
    xAxisData.value.forEach((axis) => {
      const matchItem = serviceData.find((item) => {
        return item.employeeNo === axis.employeeNo
      })
      if (matchItem) {
        matchData.push(matchItem.quantity ?? 0)
      } else {
        matchData.push(0)
      }
    })
  }
  return matchData
}
const chartRef = ref<HTMLDivElement | null>(null)
const { setOptions } = useECharts(chartRef as Ref<HTMLDivElement>, undefined, true)
const initChart = (newActivatedCustomerCountList: number[]) => {
  setOptions({
    // 悬浮窗
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#1D4CD2',
      textStyle: {
        color: 'White'
      },
      formatter: function (params) {
        const currentData = xAxisData.value[params[0].dataIndex].employeeName
        const toolsTipsResult = []
        toolsTipsResult.push(currentData)
        for (let i = 0; i < params.length; i++) {
          const currentParam = params[i]
          toolsTipsResult.push(currentParam.seriesName + '  <strong>' + (i === 2 ? currentParam.value + '%' : currentParam.value) + '</strong>')
          // toolsTipsResult.push('目标值  <strong>' + currentHoverData?.quantity + '</strong>')
          // toolsTipsResult.push('完成值  <strong>' + currentHoverData?.total + '</strong>')
          // toolsTipsResult.push(`完成率  <strong>${(currentHoverData?.total ? Number((((currentHoverData.quantity ?? 0) / currentHoverData.total) * 100).toFixed(2)) : 0)}%` + '</strong>')
        }
        return toolsTipsResult.join('<br>')
      }
    },
    /// 在这里配置线条颜色和legend颜色一致
    color: '#1D4CD2',
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: xAxisData.value.map((item) => item.employeeName)
    },
    yAxis: {
      type: 'value',
      splitLine: {
        show: true,
        lineStyle: {
          color: '#D8DFE4',
          type: 'dashed'
        }
      }
    },
    series: [
      {
        name: '激活数',
        type: 'line',
        smooth: true,
        data: newActivatedCustomerCountList
      }
    ]
  })
}

</script>
<script lang="ts">
export default {
  name: 'RegionNewActivatedCustomerCount'
}
</script>
<style scoped lang="scss">
</style>
